/**
 * TanStack Query hooks for the super-admin user list.
 *
 * One query (list all users) and one mutation (PATCH status). The
 * mutation invalidates both `["admin", "users"]` and
 * `["admin", "account-requests"]` — the latter is an over-invalidate
 * because a status change on an already-reviewed user doesn't touch
 * the pending queue, but the cost is one extra GET and the benefit
 * is simpler invariants (every admin action invalidates both caches).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { UserStatus } from "@/features/auth/useAuth";

export interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  avatar_url: string | null;
  status: UserStatus;
  is_super_admin: boolean;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  approved_by: string | null;
  last_sign_in_at: string | null;
}

/** Only `active` and `disabled` are admin-assignable via PATCH. */
export type AdminAssignableStatus = "active" | "disabled";

const ADMIN_USERS_QUERY_KEY = ["admin", "users"] as const;

export function useAllUsers() {
  return useQuery<AdminUser[]>({
    queryKey: ADMIN_USERS_QUERY_KEY,
    queryFn: () => apiFetch<AdminUser[]>("/api/admin/users"),
  });
}

export function useChangeUserStatus() {
  const queryClient = useQueryClient();
  return useMutation<AdminUser, Error, { userId: string; status: AdminAssignableStatus }>({
    mutationFn: ({ userId, status }) =>
      apiFetch<AdminUser>(`/api/admin/users/${userId}`, {
        method: "PATCH",
        body: { status },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["admin", "account-requests"] });
    },
  });
}

export { ADMIN_USERS_QUERY_KEY };
